import React from 'react';
import PropTypes from 'prop-types';
import {ButtonWithIcon, CardSkeletonLoader} from '@/components/atoms';
import {Accordion, AccordionItem} from '@/components/molecules';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import MediaPreviewModal from './MediaPreviewModal';
import MediaThumbnail from './MediaThumbnail';
import {
  GameReviewGridRow,
  GameReviewSingleRow,
  GameSectionTitle
} from './GameReviewComponents';
import {buildGameAvailabilityItems, GAME_SPACING} from './gameReviewUtils';
import ActivitySegmentsReviewSection from '@/containers/Activity/ActivityReview/components/ActivitySegmentsReviewSection';
import {formatLeaderboardLastUpdate} from '@/containers/Activity/leaderboardDateUtils';

/**
 * AccordionItem with an optional Edit button in the right accessory slot.
 */
export const EditableAccordionItem = ({
  label,
  isEditable,
  editButtonSchema,
  onEdit,
  children
}) => (
  <AccordionItem
    isDark
    expanded
    label={label}
    rightAccessoryChild={
      isEditable ? (
        <ButtonWithIcon
          uiSchema={editButtonSchema}
          iconLeading='edit'
          handleButton={onEdit}
        />
      ) : null
    }
    content={children}
  />
);

EditableAccordionItem.propTypes = {
  label: PropTypes.string.isRequired,
  isEditable: PropTypes.bool,
  editButtonSchema: PropTypes.object,
  onEdit: PropTypes.func,
  children: PropTypes.node
};

/**
 * Common outer layout shared by all game review screens.
 * Handles loading skeleton, toast alert, accordion wrapper, and media preview modal.
 */
export const GameReviewLayout = ({
  testId,
  containerClassName,
  innerClassName,
  loading,
  showToast,
  toastMessage,
  handleToastClose,
  handleViewActivity,
  previewModal,
  handleClosePreview,
  isEmpty,
  children
}) => {
  if (isEmpty) {
    return (
      <div className={`${containerClassName} w-full`}>
        <CardSkeletonLoader count={3} />
      </div>
    );
  }

  return (
    <div data-testid={testId} className={`${containerClassName} w-full`}>
      {loading && <CardSkeletonLoader count={3} />}
      {!loading && (
        <>
          {showToast && (
            <DuplicateAlert
              type='info'
              message={toastMessage}
              show={true}
              onCloseClick={handleToastClose}
              handleClick={handleViewActivity}
              buttonText='View Activity'
            />
          )}
          <div className={`${innerClassName} mt-2 w-full`}>
            <Accordion
              isDark
              size='large'
              content={
                <div className={`${GAME_SPACING.accordionGapClass} w-full`}>
                  {children}
                </div>
              }
            />
          </div>

          <MediaPreviewModal
            showModal={previewModal.show}
            onClose={handleClosePreview}
            mediaUrl={previewModal.url}
            mediaType={previewModal.type}
            title={previewModal.title}
          />
        </>
      )}
    </div>
  );
};

GameReviewLayout.propTypes = {
  testId: PropTypes.string.isRequired,
  containerClassName: PropTypes.string.isRequired,
  innerClassName: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  showToast: PropTypes.bool.isRequired,
  toastMessage: PropTypes.string,
  handleToastClose: PropTypes.func.isRequired,
  handleViewActivity: PropTypes.func.isRequired,
  previewModal: PropTypes.object.isRequired,
  handleClosePreview: PropTypes.func.isRequired,
  isEmpty: PropTypes.bool.isRequired,
  children: PropTypes.node
};

/**
 * Setup accordion content shared by game review screens.
 * Renders Activity info, Availability grid, and Segments section.
 */
export const GameReviewSetupContent = ({
  testIdPrefix,
  activityFormData,
  getFormattedDate,
  isExcluded,
  formatActivityType
}) => (
  <div className='w-full'>
    <GameSectionTitle label='Activity' className='mb-3' showDivider />
    <GameReviewGridRow
      dataTestId={`${testIdPrefix}-setup-grid-row`}
      preserveRhythm
      items={[
        {
          label: 'Activity Type',
          value: formatActivityType
            ? formatActivityType(activityFormData?.activityType)
            : activityFormData?.activityType || '-'
        },
        {
          label: 'Activity Name',
          value: activityFormData?.activityName || '-'
        },
        {isSpacer: true, label: 'setup-spacer'}
      ]}
    />

    <GameSectionTitle label='Availability' className='mb-3 mt-6' showDivider />
    <GameReviewGridRow
      dataTestId={`${testIdPrefix}-availability-grid-row`}
      items={buildGameAvailabilityItems(activityFormData, getFormattedDate)}
    />

    <ActivitySegmentsReviewSection
      activityFormData={activityFormData}
      isExcluded={isExcluded}
      RowComponent={GameReviewSingleRow}
      SectionTitleComponent={GameSectionTitle}
      testIdPrefix={testIdPrefix}
      showAllCustomersRow
      showExcludedRow
    />
  </div>
);

GameReviewSetupContent.propTypes = {
  testIdPrefix: PropTypes.string.isRequired,
  activityFormData: PropTypes.object.isRequired,
  getFormattedDate: PropTypes.func.isRequired,
  isExcluded: PropTypes.bool.isRequired,
  formatActivityType: PropTypes.func
};

/**
 * Attributes accordion content shared by game review screens.
 * Renders leaderboard data with downloadable CSV links.
 */
export const GameReviewAttributesContent = ({
  activityFormData,
  handleLeaderboardFileDownload
}) => (
  <div className='w-full'>
    {activityFormData?.leaderboards &&
    Object.keys(activityFormData.leaderboards).length > 0 ? (
      <>
        {Object.entries(activityFormData.leaderboards).map(([period, data]) => (
          <div key={period} className='mb-4'>
            <GameSectionTitle
              label={`${period} Leaderboard`}
              className='mb-3'
            />
            <GameReviewGridRow
              dataTestId={`attributes-row-${period}`}
              preserveRhythm
              items={[
                {
                  label: 'File',
                  value: data?.fileName ? (
                    data?.leaderboardFileUrl ? (
                      <button
                        type='button'
                        onClick={() =>
                          handleLeaderboardFileDownload(
                            data.leaderboardFileUrl,
                            data.fileName
                          )
                        }
                        className='bg-transparent p-0 text-[#3387df] hover:underline'>
                        {data.fileName}
                      </button>
                    ) : (
                      data.fileName
                    )
                  ) : (
                    '-'
                  )
                },
                {
                  label: 'Last Update',
                  value: formatLeaderboardLastUpdate(
                    data?.updatedAt,
                    data?.lastUpdate
                  )
                }
              ]}
            />
          </div>
        ))}
      </>
    ) : (
      <div className='text-sm text-gray-400'>No leaderboard data available</div>
    )}
  </div>
);

GameReviewAttributesContent.propTypes = {
  activityFormData: PropTypes.object.isRequired,
  handleLeaderboardFileDownload: PropTypes.func.isRequired
};

/**
 * Reusable image thumbnail row with a 3-column grid of text fields.
 * Used for Game Image and Logo Image sections in game review screens.
 */
export const GameImageWithFields = ({
  title,
  titleClassName = 'mb-3 text-sm font-bold text-white',
  className = 'mb-6',
  imageUrl,
  imageAlt,
  handlePreview,
  previewTitle,
  fields,
  thumbnailTestId,
  fieldsTestId
}) => (
  <div className={className}>
    {title && <p className={titleClassName}>{title}</p>}
    <div className='flex items-start gap-6' data-testid={thumbnailTestId}>
      <div className='shrink-0'>
        {imageUrl ? (
          <MediaThumbnail
            url={imageUrl}
            alt={imageAlt}
            type='image'
            onPreview={() => handlePreview(imageUrl, 'image', previewTitle)}
          />
        ) : (
          <div
            className='flex h-[100px] w-[100px] items-center justify-center rounded border border-[color:var(--color-border-subtle)]'
            style={{backgroundColor: 'var(--color-background-layer-02)'}}>
            <p className='text-xs text-gray-500'>No Image</p>
          </div>
        )}
      </div>
      <div
        className={`grid flex-1 ${GAME_SPACING.threeColGridClass}`}
        data-testid={fieldsTestId}>
        {fields.map((field) => (
          <div key={field.label}>
            <p className='text-sm text-[#D0D0D0]'>{field.label}</p>
            {field.isHtml && field.value !== '-' ? (
              <div
                className='font-bold text-white [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5'
                dangerouslySetInnerHTML={{__html: field.value}}
              />
            ) : (
              <p className='font-bold text-white'>{field.value}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);

GameImageWithFields.propTypes = {
  title: PropTypes.string,
  titleClassName: PropTypes.string,
  className: PropTypes.string,
  imageUrl: PropTypes.string,
  imageAlt: PropTypes.string.isRequired,
  handlePreview: PropTypes.func.isRequired,
  previewTitle: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.node
    })
  ).isRequired,
  thumbnailTestId: PropTypes.string,
  fieldsTestId: PropTypes.string
};
